import { type FC, useMemo } from "react";

import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { useSelector } from "@jti/store/reduxHooks";
import { type RootStoreState } from "@jti/store/types";
import {
  ReferralPersonalCode as ReferralPersonalCodeLib,
  type ReferralPersonalCodeIconPropsType,
} from "@jti/ui/components/ReferralPersonalCode";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import useLoggedUser from "../../hooks/useLoggedUser";
import { AccountActionButtons } from "../AccountActionButtons/AccountActionButtons";

import styles from "./ReferralPersonalCode.module.scss";

type ReferralPersonalCodeIconType = {
  url: string;
  img: string;
  trackingName: string;
};

type ReferralPersonalCodeLibProps = Parameters<typeof ReferralPersonalCodeLib>[0];

export type ReferralPersonalCodeProps = {
  topHeader?: string;
  header?: string;
  codeTitle: string;
  shareTitle: string;
  description?: string;
  variant?: "default" | "dashboard" | "withBackgroundImage";
  referralPageVariant?: boolean;
  items: ReferralPersonalCodeIconType[];
  infoTitle?: string;
  smallText?: string;
  backgroundImage?: string;
  backgroundImageTablet?: string;
  backgroundImageMobile?: string;
  signInTitle: string;
  signInUrl: string;
  registerUrl: string;
  registerTitle: string;
  isInEditor?: boolean;
  isInBuilder?: boolean;
  withBottomCta?: boolean;
  bottomCtaLabel?: string;
  bottomCtaLink?: string;
  isLegacyStyled?: boolean;
  shareConfig?: ReferralPersonalCodeLibProps["shareConfig"];
  shareCodeVariant?: ReferralPersonalCodeLibProps["shareCodeVariant"];
};

export const ReferralPersonalCode: FC<ReferralPersonalCodeProps> = ({
  topHeader,
  header,
  codeTitle,
  shareTitle,
  description,
  variant,
  referralPageVariant,
  items = [],
  infoTitle,
  backgroundImage,
  backgroundImageTablet,
  backgroundImageMobile,
  smallText,
  signInTitle,
  signInUrl,
  registerUrl,
  isInEditor,
  registerTitle,
  isInBuilder,
  withBottomCta = false,
  bottomCtaLabel = "",
  bottomCtaLink = "",
  isLegacyStyled = false,
  shareConfig,
  shareCodeVariant,
}) => {
  const [isLogged] = useLoggedUser();
  const { code } = useSelector((store: RootStoreState) => store.loyaltyCore);
  const { isReferralOn } = useConfig();
  const referralPageProps = referralPageVariant
    ? {
        showCta: !isLogged,
        ctaRow: (
          <AccountActionButtons
            signInTitle={signInTitle}
            signInUrl={signInUrl}
            registerUrl={registerUrl}
            registerTitle={registerTitle}
            position="start"
          />
        ),
      }
    : {};

  const classes = classNames(styles.wrapper, {
    [styles.withImageStyles]: variant === "withBackgroundImage",
    [styles.builderStyle]: isInBuilder,
  });

  const replaceUrlCode = (url: string, code: string, withStyles: boolean) => {
    return replaceStringPlaceholder(
      url,
      [
        {
          placeholder: "refferalCode",
          value: code,
        },
        {
          placeholder: "referralCode",
          value: code,
        },
      ],
      withStyles
    );
  };

  const itemsUrlCodeConverted = useMemo(() => {
    if (items.length === 0 || !code) return [];
    return items.map((element: ReferralPersonalCodeIconPropsType) => ({
      ...element,
      url: replaceUrlCode(element.url, code, false),
    }));
  }, [items, code]);

  if (((!isLogged && !referralPageVariant) || !isReferralOn) && !isInEditor) return null;
  return (
    <ReferralPersonalCodeLib
      code={code}
      codeTitle={codeTitle}
      shareTitle={shareTitle}
      backgroundImage={backgroundImage}
      backgroundImageTablet={backgroundImageTablet}
      backgroundImageMobile={backgroundImageMobile}
      variant={variant}
      infoTitle={infoTitle}
      referralPageVariant={referralPageVariant}
      topHeader={topHeader}
      description={description}
      header={header}
      smallText={smallText}
      items={itemsUrlCodeConverted}
      className={classes}
      withBottomCta={withBottomCta}
      bottomCtaLabel={bottomCtaLabel}
      bottomCtaLink={bottomCtaLink}
      isLegacyStyled={isLegacyStyled}
      shareConfig={shareConfig}
      shareCodeVariant={shareCodeVariant}
      {...referralPageProps}
    />
  );
};
